import React, { useEffect } from "react";
import { Page } from "../../layout/page";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import { useFormRender } from "../../hooks/useFormRender";
import { useServerApi } from "../../hooks/userServerApi";
import _ from "lodash";
import PageListHeader from "../../components/pageListHeader";
import { NumberInput, Textarea } from "@mantine/core";
import RemoteSelect2 from "../../components/remoteSelect2";
import LevelTypeTester from "../../components/levelTypeTester";
import { useTabNavigate } from "../../hooks/useTabNavigate";

const DEFAULT_LEVEL = {
  pointType: null,
  pointsPerLevel: 10000,
};

const formSchema = {
  title: "Level",
  apiEntity: "setting",
  display: {
    mode: ENUM_FORM_DISPLAY.INSIDE,
    size: "xl",
  },
  initialValues: {
    level: { ...DEFAULT_LEVEL },
  },
  validate: {
    "level.pointType": (value) => (!value ? "Required" : null),
    "level.pointsPerLevel": (value) =>
      !Number.isFinite(Number(value)) || Number(value) <= 0
        ? "Must be greater than 0"
        : null,
  },
  layout: {
    containers: [
      {
        key: "setting-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Level Conversion",
          description:
            "A consultant's Level is floor(cumulative points of the selected Point Type / points per level). There is no upper cap. Level is not stored; it follows point balances after cooling-off accrual or a manual adjustment.",
        },
      },
      {
        key: "tester-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Tester",
          description: "Preview the Level for a sample cumulative point total.",
        },
      },
    ],
    fields: [
      {
        name: "level.pointType",
        parent: "setting-section",
        component: RemoteSelect2,
        props: {
          required: true,
          label: "Point Type",
          description: "Usually Standard Premium (the cooling-off / FX / pay-year points).",
          apiEntity: "pointType",
          valueField: "_id",
          labelField: "name",
          labelRender: (data) => `${data.code || ""} ${data.name || ""}`,
          searchFields: ["code", "name", "cname"],
          clearable: true,
        },
      },
      {
        name: "level.pointsPerLevel",
        parent: "setting-section",
        component: NumberInput,
        props: {
          required: true,
          label: "Points per Level",
          description: "Cumulative points needed to gain 1 Level.",
          min: 0.01,
          precision: 2,
          parser: (value) => value?.replace(/[^0-9\-.]/g, ""),
          formatter: (value) => {
            if (value === "" || value == null) return "";
            if (Number.isNaN(parseFloat(value))) return String(value);
            const [intPart, decPart] = String(value).split(".");
            const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            return decPart !== undefined
              ? `${formattedInt}.${decPart}`
              : formattedInt;
          },
        },
      },
      {
        name: "level.remark",
        parent: "setting-section",
        component: Textarea,
        props: {
          label: "Remark",
          minRows: 2,
        },
      },
      {
        name: "levelTester",
        parent: "tester-section",
        component: LevelTypeTester,
        props: {},
      },
    ],
  },
};

const LevelType = () => {
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [renderForm, formAction] = useFormRender(formSchema);

  const fetchSetting = async () => {
    try {
      const result = await api.search({
        apiEntity: "setting",
        pageSize: 1,
      });

      if (!result || _.isEmpty(result.docs)) throw new Error("No settings");

      const setting = result.docs[0];
      formAction.open({
        mode: "edit",
        id: setting._id,
        data: {
          ...setting,
          level: {
            ...DEFAULT_LEVEL,
            ...(setting.level || {}),
          },
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  return (
    <Page>
      <PageListHeader
        title={"Level"}
        actionButtons={[
          { label: "Save", onClick: () => formAction.save() },
          {
            label: "Close",
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />
      {renderForm()}
    </Page>
  );
};

export default LevelType;
